import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Baby, ChevronRight, AlertTriangle } from 'lucide-react';

/**
 * Lightweight child snapshot for the main Dashboard.
 * Reads /api/children/snapshot. Renders nothing until we have a profile —
 * the user can still set one up via the dedicated /childrens-health route.
 */
const ChildSnapshotCard = () => {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/children/snapshot`, { withCredentials: true })
      .then((r) => { if (!cancel) setSnap(r.data); })
      .catch(() => { if (!cancel) setSnap(null); })
      .finally(() => { if (!cancel) setLoaded(true); });
    return () => { cancel = true; };
  }, []);

  if (!loaded || !snap || !snap.has_profile) return null;

  const { child, last_checkin_at, last_checkin_score, checkin_status, overdue_vaccines } = snap;
  const hasOverdue = overdue_vaccines && overdue_vaccines.length > 0;

  // Trend label from recent_checkins: simple compare first vs last.
  const trend = (() => {
    const recent = snap.recent_checkins || [];
    if (recent.length < 2) return null;
    const first = recent[0]?.score ?? null;
    const last = recent[recent.length - 1]?.score ?? null;
    if (first === null || last === null) return null;
    if (last - first >= 10) return { label: 'Trending up', tone: 'text-emerald-700' };
    if (first - last >= 10) return { label: `Down ${first - last} since ${recent.length} days ago`, tone: 'text-rose-700' };
    return null;
  })();

  const lastWhen = (() => {
    if (!last_checkin_at) return 'Not checked yet';
    const ms = Date.now() - new Date(last_checkin_at).getTime();
    const hrs = Math.floor(ms / 3_600_000);
    if (hrs < 24) return `Last check ${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `Last check ${days}d ago`;
  })();

  return (
    <button
      onClick={() => navigate('/childrens-health')}
      className="w-full mt-2 rounded-2xl bg-white border-2 border-gray-100 hover:border-sky-300 p-4 text-left flex items-center gap-3 transition-all"
      data-testid="dashboard-child-snapshot"
    >
      <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center shrink-0">
        <Baby className="h-5 w-5 text-white" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-gray-900">
          {child.name} <span className="text-gray-400 font-medium">· {child.age_label}</span>
        </span>
        <span className="block text-xs text-gray-600 mt-0.5">
          {lastWhen}{typeof last_checkin_score === 'number' ? ` · ${last_checkin_score}/100` : ''} · {checkin_status}
          {trend && <span className={`ml-1.5 ${trend.tone} font-semibold`}>· {trend.label}</span>}
        </span>
        {hasOverdue && (
          <span className="block mt-1 inline-flex items-center gap-1 text-[11px] text-rose-700 font-semibold">
            <AlertTriangle className="h-3 w-3" />
            {overdue_vaccines.length} overdue vaccine{overdue_vaccines.length === 1 ? '' : 's'}
          </span>
        )}
      </span>
      <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
    </button>
  );
};

export default ChildSnapshotCard;
